import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, EmptyState, InfoBox, Input, LoadingState, Toast, kstDateStr } from '@biznomi/ui-react'
import { getLeaveBalances, setLeaveBalance, type LeaveBalanceRow } from '@/lib/api/leave'

/**
 * 연차 부여 일수 입력 (ADMIN 전용).
 *
 * **발생 일수를 계산하지 않는다.** 근속·회계연도 기준 연차 발생은 법 해석이라
 * 우리 경계 밖이다 (§1). 회사가 정한 값을 그대로 받아 적는 화면이다.
 * 시스템이 하는 건 `부여 - 사용` 뺄셈뿐이다.
 */
export default function LeaveBalances() {
  const qc = useQueryClient()
  const thisYear = Number(kstDateStr().slice(0, 4))
  const [year, setYear] = useState(thisYear)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['leave', 'balances', year],
    queryFn: () => getLeaveBalances(year),
  })
  const rows = data?.success ? data.data ?? [] : []

  async function save(r: LeaveBalanceRow) {
    const raw = draft[r.employeeId]
    if (raw === undefined) return
    const totalDays = Number(raw)
    if (!Number.isFinite(totalDays) || totalDays < 0) {
      setError('부여 일수는 0 이상 숫자여야 합니다')
      return
    }
    setSaving(r.employeeId)
    setError('')
    const res = await setLeaveBalance({ employeeId: r.employeeId, year, totalDays })
    setSaving(null)
    if (!res.success) {
      setError(res.error ?? '저장에 실패했습니다')
      return
    }
    setDraft((d) => { const n = { ...d }; delete n[r.employeeId]; return n })
    qc.invalidateQueries({ queryKey: ['leave', 'balances', year] })
    setToast(`${r.name} 부여 ${totalDays}일 저장`)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">연차 부여</h1>

      <InfoBox tone="info">
        회사가 정한 일수를 넣어 주세요.
        남은 일수는 부여에서 사용을 뺀 값입니다.
        근속 연차는 회사가 계산해서 넣습니다.
      </InfoBox>

      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => setYear((y) => y - 1)}>‹</Button>
        <span className="font-semibold tabular-nums">{year}년</span>
        <Button variant="secondary" size="sm" onClick={() => setYear((y) => y + 1)}>›</Button>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="직원이 없습니다" />
      ) : (
        <ul className="divide-y divide-slate-100 rounded-card bg-white">
          {rows.map((r) => {
            const dirty = draft[r.employeeId] !== undefined
            return (
              <li key={r.employeeId} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.name}</p>
                  <p className="truncate text-xs text-slate-400">
                    {[r.employeeNumber, r.departmentName].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>

                <div className="shrink-0 text-right text-xs text-slate-500">
                  <p>사용 {r.usedDays}일</p>
                  {/* 음수도 그대로 보여준다. 감추면 사실과 달라진다 */}
                  <p className={r.remainingDays < 0 ? 'text-[var(--color-danger)]' : ''}>
                    남음 {r.remainingDays}일
                  </p>
                </div>

                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  aria-label={`${r.name} 부여 일수`}
                  className="w-20 shrink-0 text-right"
                  value={dirty ? draft[r.employeeId] : String(r.totalDays)}
                  onChange={(e) => setDraft((d) => ({ ...d, [r.employeeId]: e.target.value }))}
                />

                <Button
                  size="sm"
                  className="shrink-0"
                  disabled={!dirty}
                  loading={saving === r.employeeId}
                  onClick={() => void save(r)}
                >
                  저장
                </Button>
              </li>
            )
          })}
        </ul>
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
